package co.maytes.standupbot.dashboard.features.settings

import co.maytes.standupbot.shared.dto.ship.ReleaseSettingsDto

data class ReleasesSubtitle(val lead: String, val rest: String)

object ReleasesCopy {
    const val FEED_SECTION_DESCRIPTION = "Show what your engineers ship to people who do not have GitHub access."
    const val FEED_SWITCH_TITLE = "Show merged changes on the Releases page"
    const val FEED_ON_STATUS =
        "On. Merged pull requests and published releases from followed repositories appear on the Releases page " +
            "for every member and viewer. Titles and text from private repositories become visible to them."
    const val FEED_OFF_STATUS = "Off. Nothing from GitHub is shown on the Releases page."
    const val BACKFILL_TITLE = "Past merges"
    const val BACKFILL_READY = "Loads pull requests merged in the last 30 days. They appear in a minute or two."
    const val BACKFILL_BLOCKED = "Available once the feed is on and saved."
    const val REPOSITORIES_SECTION_DESCRIPTION = "Choose which repositories feed the Releases page."
    const val MODE_LABEL = "Which repositories to follow"
    const val MODE_PICKED_LABEL = "Only the repositories I pick"
    const val MODE_PICKED_DESCRIPTION = "Nothing else is followed, including repositories added later."
    const val NO_REPOSITORIES = "No GitHub repositories to choose from yet."
    const val NO_MATCHES = "No repositories match."
    const val LABELS_SECTION_DESCRIPTION = "Pull requests with these labels are never shown."
    const val LABELS_HELP = "Press Enter or comma to add a label. Pull requests carrying any of them stay out of the feed."

    private const val PREVIEW_NAMES = 3

    fun modeStandupsLabel(watchedCount: Int?): String = when (watchedCount) {
        null -> "The repositories watched for standups"
        else -> "The ${repositories(watchedCount)} watched for standups"
    }

    fun modeStandupsDescription(watchedCount: Int?): String = when {
        watchedCount == 0 -> "None are watched yet, so nothing would appear. Pick repositories instead."
        else -> "Changes to the watched list on the Integrations page carry over."
    }

    fun feedStatus(enabled: Boolean): String = if (enabled) FEED_ON_STATUS else FEED_OFF_STATUS

    fun selectedCount(selected: Int, total: Int): String = "$selected selected of $total"

    fun groupFollowed(followed: Int, total: Int): String = "$followed of $total followed"

    fun showMore(hidden: Int): String = "Show ${hidden.coerceAtMost(ReleasesRepoPicker.PAGE_SIZE)} more ($hidden hidden)"

    fun subtitle(settings: ReleaseSettingsDto, watchedCount: Int?, availableCount: Int?): ReleasesSubtitle {
        if (!settings.mergedFeedEnabled) {
            return ReleasesSubtitle("Feed off", " · merged changes are not shown on the Releases page")
        }
        val followed = settings.trackedRepos.size.takeIf { it > 0 } ?: watchedCount
        val parts = listOfNotNull(
            followingCount(followed, availableCount?.takeIf { it > 0 }),
            labelsHeldBack(settings.holdBackLabels.size),
        )
        return ReleasesSubtitle("Feed on", parts.joinToString(separator = " · ", prefix = " · "))
    }

    fun repositoryPreview(mode: ReleasesRepoMode, picked: List<String>, watchedCount: Int?): String = when {
        mode == ReleasesRepoMode.Picked && picked.isNotEmpty() -> {
            val shown = picked.take(PREVIEW_NAMES).joinToString(", ")
            val more = picked.size - PREVIEW_NAMES
            val tail = if (more > 0) " and $more more" else ""
            "The feed follows ${repositories(picked.size)}: $shown$tail."
        }
        watchedCount == null ->
            "The feed follows the repositories watched for standups."
        watchedCount > 0 && mode == ReleasesRepoMode.Picked ->
            "Nothing picked yet, so the feed follows the ${repositories(watchedCount)} watched for standups."
        watchedCount > 0 ->
            "The feed follows the ${repositories(watchedCount)} watched for standups."
        mode == ReleasesRepoMode.Picked ->
            "Nothing picked and no repositories are watched for standups, so nothing will appear in the feed. " +
                "Pick at least one."
        else ->
            "No repositories are watched for standups, so nothing will appear in the feed. Pick repositories instead."
    }

    fun holdBackPreview(labels: List<String>): String = when {
        labels.isEmpty() ->
            "No labels are held back. Pull requests that link to a security advisory are still held back " +
                "and removed if they were already stored."
        else ->
            "Pull requests labelled ${labels.joinToString(", ") { "“$it”" }} never appear in the feed and are " +
                "removed if they were already stored. Pull requests that link to a security advisory are held back too."
    }

    private fun followingCount(count: Int?, total: Int?): String? = when {
        count == null -> null
        count == 0 -> "no repositories followed"
        total != null && total >= count -> "following $count of ${repositories(total)}"
        else -> "following ${repositories(count)}"
    }

    private fun labelsHeldBack(count: Int): String = when (count) {
        0 -> "no labels held back"
        1 -> "1 label held back"
        else -> "$count labels held back"
    }

    private fun repositories(count: Int): String = if (count == 1) "1 repository" else "$count repositories"
}
